<template>
  <UiButtonIcon
    v-if="editable"
    :arialabel="title"
    class="jps-icon-button jps-sub-orange is-size-6"
    icon-size="size-6"
    :class="[fieldClass, field !== 'image' && 'list-edit-icon']"
    :icon="icon"
    :title="title"
    @click.stop="edit()"
  />
  <ValidationModal ref="validationModal" />
  <UiInputModal ref="inputModal" />
  <ImageSelectModal ref="imageSelectModal" />
</template>

<script lang="ts" setup>
import type { ImageSelectModal, UiInputModal, ValidationModal } from '#components'
import type { ModalProps } from '~/components/base/BaseModal.vue'

const EDITABLE_MAP = {
  'jps-curation-list-curation': {
    image: true,
    description: true,
  },
  'jps-curation-list-csearch': {
    image: true,
    description: true,
  },
  'jps-curation-list-database': {
    image: true,
    description: true,
  },
  'jps-curation-list-external-link': {
    title: true,
    url: true,
    description: true,
    image: true,
  },
  'jps-curation-list-image': {
    title: true,
    url: true,
    description: true,
    image: true,
  },
  'jps-curation-list-item': {
    title: true,
    linkUrl: true,
    description: true,
    image: true,
  },
  'jps-curation-list-organization': {
    description: true,
    image: true,
  },
  'jps-curation-list-search-result': {},
}

const FIELDS = {
  title: {
    icon: 'mdi/pencil',
    title: { ja: 'タイトルの設定' },
    advanced: false,
    full: true,
  },
  image: {
    icon: 'mdi/image-edit',
    title: { ja: '画像の設定' },
    advanced: true,
    full: true,
  },
  linkUrl: {
    icon: 'mdi/link',
    title: { ja: 'リンク先の設定' },
    advanced: false,
    full: true,
  },
  url: {
    icon: 'mdi/link',
    title: { ja: 'リンク先の設定' },
    advanced: false,
    full: true,
  },
  description: {
    icon: 'mdi/pencil',
    title: { ja: '解説の設定' },
    advanced: true,
    full: true,
  },
}

const EDIT_MODAL_PARAM: ModalProps = {
  background: 'none',
  closeable: false,
  animation: 'quick',
}

const { ls } = useI18n()

interface Props {
  editstate?: CurationEditState
  item: ListElement
  field: string
}
const props = withDefaults(defineProps<Props>(), {})

const editable = computed(() => {
  if (!props.item.def || !props.editstate) return false
  return (
    EDITABLE_MAP[props.item.def.type][props.field] &&
    FIELDS[props.field][props.editstate.mode]
  )
})

const icon = computed(() => {
  return FIELDS[props.field].icon
})

const title = computed(() => {
  return ls(FIELDS[props.field].title)
})

const fieldClass = computed(() => {
  if (props.field == 'image') {
    if (props.item.image) return 'edit-image-static'
  }
  return 'edit-' + props.field
})

const validationModal = ref<InstanceType<typeof ValidationModal>>()
const inputModal = ref<InstanceType<typeof UiInputModal>>()
const imageSelectModal = ref<InstanceType<typeof ImageSelectModal>>()
const edit = async () => {
  try {
    switch (props.field) {
      case 'title':
        const title = await validationModal.value.openAwait({
          title: { ja: 'タイトルの設定', en: 'Set Title' },
          target: { text: clone(props.item.title) || { ja: '', en: '' } },
          rule: { fields: { text: { field: 'text', objRuleName: 'jps-lang_string' } } },
          dialogWidth: '40rem',
        }, EDIT_MODAL_PARAM)
        if (!title) return
        props.item.def.title = title.text
        break
      case 'description':
        const description = await validationModal.value.openAwait({
          title: { ja: '説明の設定', en: 'Set Description' },
          target: { text: clone(props.item.description) || { ja: '', en: '' } },
          rule: { fields: { text: { field: 'text', objRuleName: 'jps-lang_string', allowMultiline: true } } },
          dialogWidth: '40rem',
        }, EDIT_MODAL_PARAM)
        if (!description) return
        props.item.def.description = description.text
        break
      case 'url':
        const url = await inputModal.value.openAwait({
          title: { ja: 'URLの設定', en: 'Set URL' },
          initial: props.item.def.url,
          dialogWidth: '40rem',
        }, EDIT_MODAL_PARAM)
        props.item.def.url = url
        break
      case 'linkUrl':
        const linkUrl = await inputModal.value.openAwait({
          title: { ja: 'リンク先URLの設定', en: 'Set URL' },
          message: {
            ja: '通常はメタデータに応じてリンク先が決まりますが、ここでURLを設定すると上書きすることが可能です。リンク先の特定のページに遷移したい場合などに設定してください。',
            en: '',
          },
          initial: props.item.def.linkUrl || props.item.link,
          dialogWidth: '40rem',
        }, EDIT_MODAL_PARAM)
        props.item.def.linkUrl = linkUrl
        break
      case 'image':
        const initItem = props.item.type === 'item' ? props.item.data : null
        let initImage = clone(props.item.def.image || null)
        if (!initImage && props.item.type === 'curation' && props.item.data) {
          initImage = clone((props.item.data as Curation).image)
        }
        const image = await imageSelectModal.value.openAwait({
          image: initImage, initItem
        }, EDIT_MODAL_PARAM)
        props.item.def.image = image
        break
    }
  } catch (cancel) {}
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.edit-image {
  position: absolute;
  right: 2px;
  bottom: 2px;
  border-radius: 999px;
  background-color: $white;
  width: 24px;
  height: 24px;
}
.edit-image-static {
  position: absolute;
  right: 2px;
  bottom: 2px;
  border-radius: 999px;
  background-color: $white;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;

  :deep(svg) {
    width: 1rem !important;
    height: 1rem !important;
  }
}

.list-edit-icon {
  display: inline !important;
  padding: 0px 2px !important;
  line-height: 100% !important;
}
</style>
